/**
 * The table's motion: a handful of tweens advanced by the scene's ticker. No
 * animation library — a tween is a duration, an optional delay and a function
 * of progress — and nothing moves at all when the reader has asked for reduced
 * motion, because the scene never starts one.
 */

interface Tween {
  elapsed: number;
  delay: number;
  duration: number;
  step: (p: number) => void;
  done?: (() => void) | undefined;
}

export class Motion {
  private tweens: Tween[] = [];

  /** Run `step` from progress 0 to 1 over `duration` ms, after `delay` ms. */
  add(
    duration: number,
    step: (p: number) => void,
    opts: { delay?: number; done?: () => void } = {},
  ): void {
    this.tweens.push({ elapsed: 0, delay: opts.delay ?? 0, duration, step, done: opts.done });
  }

  get busy(): boolean {
    return this.tweens.length > 0;
  }

  /** Advance every tween by `ms`. */
  tick(ms: number): void {
    const next: Tween[] = [];
    for (const t of this.tweens) {
      t.elapsed += ms;
      const p = Math.min(1, Math.max(0, (t.elapsed - t.delay) / t.duration));
      if (t.elapsed >= t.delay) t.step(p);
      if (p < 1) next.push(t);
      else t.done?.();
    }
    this.tweens = next;
  }

  /** End every tween where it would finish: the table is about to be redrawn under it. */
  finish(): void {
    const all = this.tweens;
    this.tweens = [];
    for (const t of all) {
      t.step(1);
      t.done?.();
    }
  }
}

export const easeOut = (p: number): number => 1 - (1 - p) ** 3;
export const easeInOut = (p: number): number =>
  p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2;
